import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../../lib/cms';

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'SoftwareApplication',
      name: 'RevTech InsightFlow',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'Dental revenue cycle analytics platform for U.S. DSOs. Real-time AR dashboards, payer performance reporting, multi-location roll-up analytics, and production-to-collection waterfall — integrated with NDS DataVault and Xentrios PMS.',
      offers: {
        '@type': 'Offer',
        url: 'https://xentrios.com/demo',
      },
      provider: {
        '@type': 'Organization',
        name: 'RevTech Partners',
        url: 'https://revtechpartners.com',
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is a dental revenue cycle analytics platform?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A dental revenue cycle analytics platform aggregates claim, payment, production, and payer data from across your dental group and surfaces it in real-time dashboards and reports. Unlike built-in PMS reports — which show single-location data for the current period — a purpose-built analytics platform provides enterprise AR roll-up across all locations, payer performance benchmarking over time, production-to-collection waterfall analysis, denial root cause dashboards, and Power BI integration for custom reporting. For DSOs, this level of visibility is the difference between managing by instinct and managing by data.',
          },
        },
        {
          '@type': 'Question',
          name: 'How does InsightFlow differ from the reports already inside my PMS?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `PMS built-in reports are designed for single-location, single-period lookups by office managers and billing staff. They show you what's in the system right now but don't support cross-location comparison, multi-period trending, payer performance benchmarking, or denial root cause analysis. InsightFlow uses the NDS DataVault integration layer to pull normalized data from all your PMS environments — regardless of whether you're running Dentrix, Eaglesoft, Open Dental, or CareStack at different locations — and presents it in enterprise dashboards that update in real time. The result is a single analytics environment for your entire revenue cycle, regardless of how many PMS systems or locations you have.`,
          },
        },
        {
          '@type': 'Question',
          name: 'What is a production-to-collection waterfall in dental analytics?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The production-to-collection waterfall is an analytical view that starts with total production (all procedures completed), then applies each reduction step in sequence: contractual adjustments, write-offs, unsubmitted claims, denied claims, unresolved AR, and finally arrives at net collections. Each step in the waterfall is a separate measurement opportunity. For a DSO, seeing where production is lost — whether at the contractual adjustment level, the denial level, or the aged AR level — identifies where process improvement will have the greatest revenue impact. InsightFlow renders this waterfall at enterprise, location, provider, and payer levels.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can InsightFlow connect to Power BI for custom reporting?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. InsightFlow is built on the NDS DataVault architecture, which exposes a normalized dental data warehouse that connects directly to Power BI, Tableau, and other BI tools. Your data team can build custom reports on top of the same clean, consistent data that drives the InsightFlow dashboards — without needing to write custom SQL against your PMS database or maintain a separate ETL pipeline. NDS DataVault handles the extraction and normalization from all your PMS environments; Power BI sees a single unified dataset.',
          },
        },
      ],
    },
  ],
};

const features = [
  {
    icon: '📊',
    name: 'Multi-location AR Roll-up',
    desc: 'Enterprise AR aging dashboard across all locations, payers, and providers — updated in real time. Filter by location, provider, payer, or CDT code category. Drill from group-level totals to individual claim detail without leaving the view.',
  },
  {
    icon: '🤝',
    name: 'Payer Performance Benchmarking',
    desc: `Track every payer's average days to payment, denial rate, underpayment rate, and contract compliance over time. Compare payers head-to-head and identify which contracts are underperforming your network averages — with the data to support renegotiation.`,
  },
  {
    icon: '🌊',
    name: 'Production-to-Collection Waterfall',
    desc: 'See exactly where production dollars are lost between procedure completion and collected payment. Contractual adjustments, write-offs, denied claims, and aged AR — all quantified and trended at location, provider, and payer level.',
  },
  {
    icon: '🔍',
    name: 'Denial Root Cause Analytics',
    desc: 'Aggregate denial data across all locations and surface systemic root causes: which CDT codes are consistently downgraded by which payers, which front-desk workflows produce eligibility mismatches, and which providers have outlier denial rates.',
  },
  {
    icon: '📡',
    name: 'Real-time Claim Status',
    desc: 'Live claim status tracking from submission through adjudication — without logging into clearinghouse portals. InsightFlow surfaces pending, pended, and denied claims with aging indicators so your AR team always knows what needs attention.',
  },
  {
    icon: '⚡',
    name: 'Power BI Integration via DataVault',
    desc: 'NDS DataVault exposes a normalized dental data warehouse that connects directly to Power BI and Tableau. Your analytics team builds custom reports on clean, consistent, cross-location data without managing a separate ETL pipeline.',
  },
];

const integrations = ['Dentrix', 'Eaglesoft', 'Open Dental', 'CareStack', 'Planet DDS', 'Xentrios'];

export default function DentalAnalyticsPlatformPage() {
  const cmsPage = useCmsPage('xentrios', 'dental-analytics-platform');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'Dental Revenue Cycle Analytics Platform');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'RevTech Partners, National Data Solutions, and Xentrios provide an integrated dental operating intelligence stack for DSOs: RCM automation, denial management, reconciliation, analytics, data warehousing, PMS integration, and cloud-native practice management.');
  const featureItems = c(cmsPage, 'features', 'items', features);
  const integrationItems = c(cmsPage, 'integrations', 'items', integrations);
  return (
    <div>
      <Helmet>
        <title>Dental Revenue Cycle Analytics Platform</title>
        <meta
          name="description"
          content="RevTech InsightFlow is a dental revenue cycle analytics platform for U.S. DSOs. Real-time AR dashboards, payer performance reporting, multi-location roll-up analytics, and production-to-collection waterfall — integrated with NDS DataVault and Xentrios PMS."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Dental Analytics Platform</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>
              Request a Demo
            </Link>
            <Link
              to="/stack"
              className="xen-btn xen-btn-outline"
              style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}
            >
              See the Full Stack
            </Link>
          </div>
        </div>
      </section>

      {/* What dental RCM analytics should do */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">The Category</p>
          <h2>What dental RCM analytics should actually do</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            Your PMS has reports. That's not the same as analytics. Here's the difference.
          </p>
          <div className="xen-two-col" style={{ gap: '3rem' }}>
            <div>
              <p>
                Every practice management system ships with a set of reports — AR aging, daily production,
                collection totals. These reports are useful for office managers running a single location.
                They answer the question "what happened today at this office?" But for a DSO operating
                10, 50, or 200 locations, that question is the wrong starting point.
              </p>
              <p style={{ marginTop: '1rem' }}>
                The questions that matter at the enterprise level are different. Which payers are paying
                at contracted rates and which are systematically underpaying? Which locations have an
                AR aging profile that's drifting outside acceptable bounds? Where in the production-to-collection
                waterfall is the group losing the most revenue — and is it a front-end problem, a clinical
                coding problem, a denial problem, or a collections problem?
              </p>
              <p style={{ marginTop: '1rem' }}>
                PMS built-in reports can't answer these questions because they're scoped to a single location,
                a single period, and a single PMS data model. A DSO that has acquired practices running
                Dentrix, Eaglesoft, and Open Dental at different sites has three completely separate data
                environments — and no native path to a consolidated enterprise view.
              </p>
            </div>
            <div>
              <p>
                Real dental RCM analytics requires a data layer that normalizes data across PMS environments
                — which is what the NDS DataVault provides — and an analytics layer that models dental
                revenue cycle processes specifically, not generic healthcare billing. That means understanding
                CDT code structures, dental payer adjudication logic, fee schedule relationships, and the
                specific KPIs that matter to a dental CFO or RCM director.
              </p>
              <p style={{ marginTop: '1rem' }}>
                InsightFlow is that analytics layer. It connects to the NDS DataVault, ingests normalized
                claim, production, payment, and denial data from all your locations, and surfaces it in
                dashboards and reports designed for dental RCM leadership. AR roll-up across every location.
                Payer performance trending over 12 months. Production-to-collection waterfalls at the
                provider level. Denial root cause dashboards that show which CDT codes are driving which
                denial reasons at which payers.
              </p>
              <p style={{ marginTop: '1rem' }}>
                And for organizations that want to build on top of the data warehouse directly — connecting
                Power BI or Tableau to the NDS DataVault for custom executive reporting — that path is
                built in with no additional ETL work required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* InsightFlow feature grid */}
      <section className="xen-section xen-section-center" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">InsightFlow Features</p>
          <h2>Enterprise dental analytics, built for the revenue cycle.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            InsightFlow covers every analytical dimension of the dental revenue cycle — from real-time
            claim status to multi-year payer performance trends.
          </p>
          <div className="xen-features">
            {featureItems.map((f) => (
              <div key={f.name} className="xen-feature" style={{ textAlign: 'left' }}>
                <div className="xen-feature-icon" style={{ fontSize: '1.75rem' }}>{f.icon}</div>
                <h3 style={{ color: 'var(--xen-navy)' }}>{f.name}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beyond PMS reports — two-col comparison */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">The Comparison</p>
          <h2>Beyond PMS reports</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Built-in PMS reports answer single-location billing questions. InsightFlow + DataVault answer
            enterprise revenue cycle questions.
          </p>
          <div className="xen-two-col" style={{ gap: '2.5rem' }}>
            <div
              style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '2rem',
                boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                borderTop: '4px solid #e57373',
              }}
            >
              <h3 style={{ color: '#c62828', marginBottom: '1.25rem' }}>What built-in PMS reports give you</h3>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: 1.9, color: '#555' }}>
                <li>AR aging for one location, in one PMS, today</li>
                <li>Daily production and collection totals by provider</li>
                <li>Insurance aging report — sorted by carrier name</li>
                <li>Manual export required for every reporting period</li>
                <li>No cross-location comparison without spreadsheet consolidation</li>
                <li>No payer performance trending over time</li>
                <li>No production-to-collection waterfall view</li>
                <li>No denial root cause analysis or CARC-level reporting</li>
                <li>Incompatible data models across different PMS platforms</li>
                <li>No connection to BI tools without custom SQL or middleware</li>
              </ul>
            </div>
            <div
              style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '2rem',
                boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                borderTop: '4px solid var(--xen-teal)',
              }}
            >
              <h3 style={{ color: '#1b5e20', marginBottom: '1.25rem' }}>What InsightFlow + DataVault give you</h3>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: 1.9, color: '#555' }}>
                <li>Enterprise AR roll-up across all locations, all PMS platforms, real time</li>
                <li>Payer performance benchmarking with 12-month trending</li>
                <li>Production-to-collection waterfall at group, location, and provider level</li>
                <li>Denial root cause analytics by CARC code, CDT code, and payer</li>
                <li>Real-time claim status without logging into clearinghouse portals</li>
                <li>Cross-location comparison with anomaly flagging</li>
                <li>Normalized data warehouse that works across Dentrix, Eaglesoft, Open Dental, CareStack</li>
                <li>Direct Power BI and Tableau connectivity — no custom ETL</li>
                <li>Role-based access: location managers see their data, executives see everything</li>
                <li>Historical data preservation for acquisition due diligence and benchmarking</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Integration badges */}
      <section className="xen-section xen-section-center" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Integrations</p>
          <h2>Connects to every major dental PMS.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            InsightFlow analytics are powered by the NDS DataVault integration layer — which normalizes
            data from every major U.S. dental PMS into a single clean data warehouse. No manual exports.
            No spreadsheet consolidation. No custom middleware.
          </p>
          <div className="xen-integrations">
            {integrationItems.map((name) => (
              <div key={name} className="xen-integration-badge">
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container">
          <h2>See InsightFlow analytics for your DSO.</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
            Tell us your current PMS environment and location count. We'll show you what enterprise-level
            dental analytics looks like on your own data — including where your production-to-collection
            waterfall is leaking.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
}
